import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { AIProject } from '../../../../mocks/aiProjects';
import { createAIProjectUrl } from '../../../../hooks/useNavigation';
import { useAuth } from '../../../../contexts/AuthContext';
import { AuthModal } from '../../../../components/feature/AuthModal';

interface ProjectSidebarProps {
  project: AIProject;
  relatedProjects: AIProject[];
}

const ProjectSidebar = ({ project, relatedProjects }: ProjectSidebarProps) => {
  const { user } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [isFollowing, setIsFollowing] = useState(false);

  const handleFollow = () => {
    if (!user) {
      setShowAuthModal(true);
      return;
    }
    setIsFollowing(!isFollowing);
  };

  return (
    <div className="space-y-6">
      {/* Creator Card */}
      <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-5">
        <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-4">Creator</h3>
        <div className="flex items-center gap-3 mb-4">
          <img
            src={project.creator.avatar}
            alt={project.creator.name}
            className="w-12 h-12 rounded-full object-cover border-2 border-slate-600"
          />
          <div>
            <div className="flex items-center gap-1.5">
              <span className="font-semibold text-white">{project.creator.name}</span>
              {project.creator.verified && (
                <i className="ri-verified-badge-fill text-orange-500"></i>
              )}
            </div>
            <span className="text-xs text-slate-400">{project.creator.followers} followers</span>
          </div>
        </div>
        <p className="text-sm text-slate-400 leading-relaxed mb-4">{project.creator.bio}</p>
        <button
          onClick={handleFollow}
          className={`w-full py-2.5 text-sm font-semibold rounded-lg transition-all cursor-pointer whitespace-nowrap ${
            isFollowing
              ? 'bg-slate-700 text-slate-300 border border-slate-600 hover:bg-slate-600'
              : 'bg-gradient-to-r from-orange-500 to-rose-500 hover:from-orange-600 hover:to-rose-600 text-white'
          }`}
        >
          {isFollowing ? 'Following' : 'Follow Creator'}
        </button>
      </div>

      {/* Project Details */}
      <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-5">
        <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-4">Project Details</h3>
        <div className="space-y-3">
          {[
            { label: 'Category', value: project.category, icon: 'ri-folder-line' },
            { label: 'Style', value: project.style, icon: 'ri-palette-line' },
            { label: 'Resolution', value: project.resolution, icon: 'ri-aspect-ratio-line' },
            { label: 'Frame Rate', value: `${project.fps} fps`, icon: 'ri-speed-line' },
            { label: 'Aspect Ratio', value: project.aspectRatio, icon: 'ri-layout-masonry-line' },
            { label: 'Published', value: new Date(project.datePublished).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }), icon: 'ri-calendar-line' },
          ].map((item) => (
            <div key={item.label} className="flex items-center justify-between py-2 border-b border-slate-700/50 last:border-0">
              <span className="flex items-center gap-2 text-sm text-slate-400">
                <i className={`${item.icon} text-slate-500`}></i>
                {item.label}
              </span>
              <span className="text-sm font-medium text-white">{item.value}</span>
            </div>
          ))}
        </div>
      </div>

      {/* AI Tools Used */}
      <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-5">
        <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-4">AI Tools Used</h3>
        <div className="flex flex-wrap gap-2">
          {project.tools.map((tool, index) => (
            <span
              key={index}
              className="px-3 py-1.5 bg-slate-700/50 text-slate-300 text-sm rounded-lg border border-slate-600"
            >
              {tool}
            </span>
          ))}
        </div>
      </div>

      {/* Awards */}
      {project.awards.length > 0 && (
        <div className="bg-gradient-to-br from-amber-500/10 to-orange-500/10 rounded-xl border border-amber-500/30 p-5">
          <h3 className="text-sm font-semibold text-amber-400 uppercase tracking-wider mb-4 flex items-center gap-2">
            <i className="ri-trophy-fill"></i>
            Awards
          </h3>
          <div className="space-y-2">
            {project.awards.map((award, index) => (
              <div key={index} className="flex items-center gap-2 py-2">
                <div className="w-8 h-8 flex items-center justify-center bg-amber-500/20 rounded-lg">
                  <i className="ri-medal-line text-amber-400"></i>
                </div>
                <span className="text-sm font-medium text-amber-200">{award}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tags */}
      <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-5">
        <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-4">Tags</h3>
        <div className="flex flex-wrap gap-2">
          {project.tags.map((tag, index) => (
            <span
              key={index}
              className="px-3 py-1 bg-orange-500/10 text-orange-400 text-xs rounded-full border border-orange-500/20 cursor-pointer hover:bg-orange-500/20 transition-colors"
            >
              #{tag}
            </span>
          ))}
        </div>
      </div>

      {/* Related Projects */}
      {relatedProjects.length > 0 && (
        <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-5">
          <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-4">Related Projects</h3>
          <div className="space-y-4">
            {relatedProjects.map((rp) => (
              <Link
                key={rp.id}
                to={createAIProjectUrl(rp.slug)}
                className="flex gap-3 group"
              >
                <div className="relative w-28 h-16 flex-shrink-0 rounded-lg overflow-hidden">
                  <img
                    src={rp.thumbnail}
                    alt={rp.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <span className="absolute bottom-1 right-1 px-1.5 py-0.5 bg-black/70 rounded text-white text-[10px]">
                    {rp.duration}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-medium text-white group-hover:text-orange-400 transition-colors line-clamp-1">
                    {rp.title}
                  </h4>
                  <p className="text-xs text-slate-400 mt-0.5">{rp.creator.name}</p>
                  <div className="flex items-center gap-2 mt-1 text-xs text-slate-500">
                    <span>{rp.views} views</span>
                    <span>•</span>
                    <span>{rp.category}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        initialMode="login"
      />
    </div>
  );
};

export default ProjectSidebar;
